import { createFileRoute, Outlet } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/app-shell';
import { useAuth } from '@/hooks/use-auth';
import { useWorkspaces } from '@/hooks/use-workspaces';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useEffect, useState, useCallback } from 'react';
import { CommandPalette } from '@/components/command-palette/command-palette';
import { GlobalQuickAdd } from '@/components/quick-add/global-quick-add';
import { BrainDumpModal } from '@/components/brain-dump/brain-dump-modal';
import { WeeklyReviewModal } from '@/components/weekly-review/weekly-review-modal';
import { ShortcutReferenceModal } from '@/components/shortcuts/shortcut-reference-modal';
import { ErrorBoundary } from '@/components/common/error-boundary';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { setSentryUser } from '@/lib/sentry';

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { data: workspaces } = useWorkspaces();
  const { activeWorkspaceId, setActiveWorkspace } = useWorkspaceStore();
  const [brainDumpOpen, setBrainDumpOpen] = useState(false);
  const [weeklyReviewOpen, setWeeklyReviewOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const handleOpenSearch = useCallback(() => {
    document.dispatchEvent(new CustomEvent('triankl:open-search'));
  }, []);

  const { shortcutRefOpen, setShortcutRefOpen } = useKeyboardShortcuts({
    onOpenSearch: handleOpenSearch,
    onOpenQuickAdd: () => setQuickAddOpen(true),
  });

  // Set default active workspace
  useEffect(() => {
    if (workspaces && workspaces.length > 0 && !activeWorkspaceId) {
      setActiveWorkspace(workspaces[0].id);
    }
  }, [workspaces, activeWorkspaceId, setActiveWorkspace]);

  // Open quick-add from mobile FAB custom event
  useEffect(() => {
    const handler = () => setQuickAddOpen(true);
    document.addEventListener('triankl:open-quick-add', handler);
    return () => document.removeEventListener('triankl:open-quick-add', handler);
  }, []);

  // Set Sentry user context
  useEffect(() => {
    if (user) {
      setSentryUser({ id: user.id, email: user.email });
    }
  }, [user]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = '/login';
    return null;
  }

  return (
    <>
      <AppShell>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </AppShell>
      <CommandPalette
        onBrainDump={() => setBrainDumpOpen(true)}
        onWeeklyReview={() => setWeeklyReviewOpen(true)}
        onQuickAdd={() => setQuickAddOpen(true)}
      />
      <GlobalQuickAdd open={quickAddOpen} onOpenChange={setQuickAddOpen} />
      <BrainDumpModal open={brainDumpOpen} onOpenChange={setBrainDumpOpen} />
      <WeeklyReviewModal open={weeklyReviewOpen} onOpenChange={setWeeklyReviewOpen} />
      <ShortcutReferenceModal open={shortcutRefOpen} onOpenChange={setShortcutRefOpen} />
    </>
  );
}
